import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import {
  Em,
  GhostButton,
  GradientButton,
  Headline,
  Panel,
  Section,
  SERIF,
  Subhead,
} from "../components/marketing/primitives";
import { ARTICLES, Article, ArticleBlock, findArticle } from "../components/marketing/articles";
import { ArticleFigure } from "../components/marketing/articleFigures";
import { usePageMeta } from "../components/marketing/usePageMeta";

/**
 * /insights: article index, and /insights/:slug: one article.
 *
 * Long-form reading layout: narrow measure, serif headings, sans body at a
 * comfortable size. No Reveal animation on article text; readers scroll fast
 * and content must never be blank.
 */
export function InsightsIndex() {
  usePageMeta(
    "Insights | Paxley",
    "Analysis from the Paxley team on software supply chain attacks, code security and what engineering teams can do about them."
  );
  return (
    <MarketingLayout>
      <Section padding="hero" container="narrow">
        <Headline size="hero">
          Insights, <Em>without the hype.</Em>
        </Headline>
        <Subhead size="large" className="mt-6 max-w-xl">
          What actually happened, what would have helped, and what would not.
        </Subhead>
        <ul className="mt-12">
          {ARTICLES.map((a) => (
            <li key={a.slug} className="border-t border-[#E4DFD3] py-8">
              <Link to={`/insights/${a.slug}`} className="group block">
                <p className="font-mono text-xs opacity-50">
                  {a.date} · {a.readMinutes} min read
                </p>
                <h2
                  className="mt-2 text-2xl leading-[1.15] group-hover:underline md:text-3xl"
                  style={SERIF}
                >
                  {a.title}
                </h2>
                <p className="mt-3 leading-relaxed opacity-75">{a.dek}</p>
              </Link>
            </li>
          ))}
        </ul>
      </Section>
    </MarketingLayout>
  );
}

export function InsightArticle() {
  const { slug } = useParams();
  const article = findArticle(slug);
  usePageMeta(
    article ? `${article.title} | Paxley` : "Article not found | Paxley",
    article ? article.dek : "This article does not exist."
  );
  if (!article) {
    return (
      <MarketingLayout>
        <Section padding="hero" container="narrow">
          <Headline size="section">That article does not exist.</Headline>
          <div className="mt-8">
            <GhostButton to="/insights">All insights</GhostButton>
          </div>
        </Section>
      </MarketingLayout>
    );
  }
  return (
    <MarketingLayout>
      <ArticleBody article={article} />
      <Sources article={article} />
      <ArticleCTA article={article} />
    </MarketingLayout>
  );
}

function ArticleBody({ article }: { article: Article }) {
  return (
    <Section padding="hero" container="narrow">
      <Link
        to="/insights"
        className="inline-flex items-center gap-1.5 text-sm opacity-60 hover:opacity-100"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Insights
      </Link>
      <h1 className="mt-6 text-4xl leading-[1.1] tracking-[-0.01em] md:text-5xl" style={SERIF}>
        {article.title}
      </h1>
      <p className="mt-5 text-lg leading-relaxed opacity-75 md:text-xl">{article.dek}</p>
      <p className="mt-6 border-b border-[#E4DFD3] pb-6 text-sm opacity-60">
        {article.author}
        {article.role ? `, ${article.role}` : ""} · {article.date} · {article.readMinutes} min read
      </p>
      <div className="mt-8">
        {article.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </div>
    </Section>
  );
}

function Block({ block }: { block: ArticleBlock }) {
  switch (block.kind) {
    case "p":
      return <p className="mt-5 text-[1.075rem] leading-[1.75] opacity-90">{block.text}</p>;
    case "h2":
      return (
        <h2 className="mt-12 text-2xl leading-[1.2] md:text-[1.9rem]" style={SERIF}>
          {block.text}
        </h2>
      );
    case "quote":
      return (
        <blockquote
          className="mt-8 border-l-2 border-[#1E4D38] pl-5 text-xl italic leading-relaxed"
          style={SERIF}
        >
          {block.text}
        </blockquote>
      );
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag
          className={`mt-5 space-y-3 pl-6 text-[1.075rem] leading-[1.7] opacity-90 ${
            block.ordered ? "list-decimal" : "list-disc"
          }`}
        >
          {block.items.map((item) => (
            <li key={item} className="pl-1">
              {item}
            </li>
          ))}
        </ListTag>
      );
    }
    case "figure":
      return <ArticleFigure name={block.name} caption={block.caption} />;
    case "code":
      return (
        <pre className="mt-5 overflow-x-auto rounded-xl bg-[#141412] px-5 py-4 font-mono text-[0.85rem] leading-relaxed text-[#E4EFE4]">
          <code>{block.text}</code>
        </pre>
      );
    case "facts":
      return (
        <dl className="mt-8 divide-y divide-[#E4DFD3] rounded-2xl border border-[#E4DFD3] bg-white">
          {block.items.map((f) => (
            <div key={f.k} className="grid gap-1 px-5 py-4 sm:grid-cols-[8rem_1fr] sm:gap-4">
              <dt className="font-mono text-xs font-semibold uppercase tracking-wider text-[#1E4D38] sm:pt-1">
                {f.k}
              </dt>
              <dd className="text-[0.98rem] leading-relaxed">{f.v}</dd>
            </div>
          ))}
        </dl>
      );
    case "table":
      return (
        <div className="mt-6">
          <div className="overflow-x-auto rounded-2xl border border-[#E4DFD3] bg-white">
            <table className="w-full text-left text-[0.98rem]">
              <thead>
                <tr className="bg-[#1E4D38] text-[#FBF8F1]">
                  {block.head.map((h) => (
                    <th key={h} className="px-5 py-3 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((r) => (
                  <tr key={r.join("|")} className="border-t border-[#E4DFD3]">
                    {r.map((c, i) => (
                      <td key={i} className={`px-5 py-2.5 ${i > 0 ? "font-mono" : ""}`}>
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.note && <p className="mt-3 text-sm leading-relaxed opacity-65">{block.note}</p>}
        </div>
      );
    case "callout":
      return (
        <div className="mt-10 rounded-2xl bg-[#E4EFE4] px-6 py-6 sm:px-8">
          <p className="text-xl" style={SERIF}>
            {block.title}
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-[0.98rem] leading-relaxed">
            {block.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      );
  }
}

function Sources({ article }: { article: Article }) {
  return (
    <Section padding="tight" container="narrow">
      <p className="text-lg" style={SERIF}>
        Sources
      </p>
      <ul className="mt-3 space-y-1.5">
        {article.sources.map((s) => (
          <li key={s.url}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm opacity-65 underline-offset-2 hover:underline hover:opacity-100"
            >
              {s.label}
              <ArrowUpRight className="h-3 w-3 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-xs leading-relaxed opacity-50">
        Incident details are as reported by the sources above at the time of writing and may be
        revised as investigations continue.
      </p>
    </Section>
  );
}

function ArticleCTA({ article }: { article: Article }) {
  const cta = article.cta ?? {
    title: "Are you affected?",
    em: "Find out today.",
    body: "Scan up to three repositories free: your code, dependencies and secrets in one pass.",
  };
  return (
    <Section container="none" padding="tight">
      <Panel tone="green">
        <div className="mx-auto max-w-3xl px-6 py-14 text-center sm:px-10 md:py-16">
          <Headline size="section">
            {cta.title} <Em>{cta.em}</Em>
          </Headline>
          <Subhead className="mx-auto mt-4 max-w-xl !opacity-80">
            {cta.body}
          </Subhead>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <GradientButton to="/onboarding" size="lg" className="!bg-[#F5F1E6] !text-[#17150F]">
              Start free scan
            </GradientButton>
            <GhostButton to="/inquiry?interest=trial" size="lg">
              Request a guided trial
            </GhostButton>
          </div>
        </div>
      </Panel>
    </Section>
  );
}
